import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { History, Play, FolderOpen, Loader2 } from 'lucide-react';
import { uploadManager, resumeDeadline } from '@/lib/uploadManager';
import { useUploadManagerState } from '@/hooks/useUploadManager';
import { formatBytes } from '@/lib/upload';
import { formatDateTime } from '@/lib/dates';
import { useToast } from '@/components/Toast';

/**
 * Uploads a reload (or a closed tab) cut short, with the way back in.
 *
 * Chromium keeps a handle to the original file, so Resume is one click (the
 * browser may ask once to allow reading it again). Other browsers cannot
 * reopen a file after a reload, so the user picks it again and it is matched
 * by name, size and modified time — then continues from the last finished
 * chunk rather than from zero.
 */
export function interruptedTitle(t: TFunction, list: Array<{ fileName: string }>): string {
  return list.length === 1
    ? t('upload.resume.titleOne', '"{name}" was interrupted', { name: list[0].fileName })
    : t('upload.resume.titleMany', '{n} uploads were interrupted', { n: list.length });
}

export default function InterruptedUploads({ variant = 'band', showTitle = true }: {
  variant?: 'band' | 'panel';
  /** Off when the surrounding header already says how many were interrupted. */
  showTitle?: boolean;
}) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { interrupted } = useUploadManagerState();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  if (interrupted.length === 0) return null;

  const withHandle = interrupted.filter((u) => u.hasHandle);
  const deadlines = interrupted.map((u) => resumeDeadline(u.startedAt)).filter((d): d is number => d !== null);
  const until = deadlines.length ? Math.min(...deadlines) : null;

  async function resumeOne(key: string, name: string, permission?: string): Promise<boolean> {
    setBusyKey(key);
    try {
      const r = await uploadManager.resumeInterrupted(key, permission);
      if (r === 'resumed') return true;
      if (r === 'denied') toast(t('upload.resume.denied', 'Soltik needs permission to read "{name}" again. Click Resume and choose Allow.', { name }), 'warning', 6000);
      else if (r === 'missing') toast(t('upload.resume.missing', '"{name}" was moved or deleted. Choose it again to resume.', { name }), 'warning', 6000);
      else if (r === 'changed') toast(t('upload.resume.changed', '"{name}" changed since the upload started, so it cannot continue. Upload it again instead.', { name }), 'warning', 7000);
      else inputRef.current?.click();
      return false;
    } finally {
      setBusyKey(null);
    }
  }

  async function resumeAll() {
    // Every permission is asked inside the click, before any await — after the
    // first await the gesture is gone and later asks would be refused.
    const asked = uploadManager.requestResumePermissions(withHandle.map((u) => u.key));
    const answers = await asked;
    for (const u of withHandle) {
      await resumeOne(u.key, u.fileName, answers.get(u.key));
    }
  }

  function onPicked(files: FileList | null) {
    if (!files || files.length === 0) return;
    const { unmatched } = uploadManager.resumeWithFiles(Array.from(files));
    if (unmatched.length > 0) {
      toast(t('upload.resume.unmatched', '{n} chosen files did not match an interrupted upload. Pick the same file (same name and size).', { n: unmatched.length }), 'warning', 7000);
    }
  }

  const shell = variant === 'panel'
    ? 'px-3 py-2.5 border-t border-sky-500/20 bg-sky-500/8 text-xs text-sky-100'
    : 'border-b border-sky-500/30 bg-sky-500/10 px-4 py-2.5 text-xs text-sky-100';
  const btn = 'inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-medium cursor-pointer disabled:opacity-60';

  return (
    <div className={shell}>
      <div className="flex items-start gap-2">
        <History size={14} className="text-sky-300 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          {showTitle && (
            <div className="font-medium mb-0.5">
              {interruptedTitle(t, interrupted)}
            </div>
          )}
          <div className="text-sky-200/60">
            {withHandle.length === interrupted.length
              ? t('upload.resume.hintHandle', 'Resume continues from where it stopped.')
              : t('upload.resume.hintPick', 'Choose the same file again (or re-add it in the Library) and it continues from where it stopped.')}
            {' '}
            {until
              ? t('upload.resume.windowUntil', 'Resumable for 48 hours after starting, until {date}.', { date: formatDateTime(until) })
              : t('upload.resume.window', 'Resumable for 48 hours after starting.')}
          </div>
        </div>
      </div>

      <ul className="mt-2 space-y-1 max-h-40 overflow-y-auto pe-1">
        {interrupted.map((u) => (
          <li key={u.key} className="flex items-center gap-2">
            <span className="truncate flex-1 min-w-0 text-[11px] text-sky-100/80" title={u.folder ? `${u.fileName} → ${u.folder}` : u.fileName}>
              {u.fileName}
            </span>
            <span className="text-[10px] tabular-nums text-sky-200/50 shrink-0">{formatBytes(u.fileSize)}</span>
            {u.hasHandle && (
              <button
                type="button"
                onClick={() => void resumeOne(u.key, u.fileName)}
                disabled={busyKey !== null}
                className="text-sky-200/70 hover:text-sky-50 shrink-0 cursor-pointer disabled:opacity-50"
                aria-label={t('upload.resume.resumeOne', 'Resume {name}', { name: u.fileName })}
                title={t('upload.resume.resumeOne', 'Resume {name}', { name: u.fileName })}
              >
                {busyKey === u.key ? <Loader2 size={12} className="animate-spin" /> : <Play size={12} />}
              </button>
            )}
          </li>
        ))}
      </ul>

      <div className="mt-2 flex flex-wrap items-center gap-2">
        {withHandle.length > 0 && (
          <button
            type="button"
            onClick={() => void resumeAll()}
            disabled={busyKey !== null}
            className={`${btn} bg-sky-400 text-black hover:bg-sky-300`}
          >
            <Play size={11} />
            {withHandle.length === 1
              ? t('upload.resume.resume', 'Resume')
              : t('upload.resume.resumeAll', 'Resume {n}', { n: withHandle.length })}
          </button>
        )}
        {withHandle.length < interrupted.length && (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className={`${btn} ${withHandle.length > 0 ? 'bg-white/8 text-sky-100 hover:bg-white/12' : 'bg-sky-400 text-black hover:bg-sky-300'}`}
          >
            <FolderOpen size={11} />
            {t('upload.resume.choose', 'Choose file to resume')}
          </button>
        )}
        <button
          type="button"
          onClick={() => void uploadManager.discardInterrupted()}
          className="ms-auto text-[11px] text-sky-200/70 hover:text-sky-50 cursor-pointer"
        >
          {t('upload.resume.discard', 'Discard')}
        </button>
      </div>
      <input
        ref={inputRef}
        type="file"
        multiple
        className="hidden"
        onChange={(e) => { onPicked(e.target.files); e.target.value = ''; }}
      />
    </div>
  );
}
